<template>
  <div class="project-card__container">
    <div
      class="project-card__status"
      :style="{ 'background-color': project.color || DEFAULT_COLOR }"
    />
    <div class="project-card">
      <router-link
        :to="getProjectLink(project)"
        tag="div"
        class="project-card_main pointer"
        @click="$emit('open')"
      >
        <div class="project-card__info spacey-8">
          <router-link
            :to="getProjectLink(project)"
            class="project-card__title vertical-centering grey-900 text-bs spacex-8 no-link"
          >
            {{ project.name }}
            <InfoTooltip top>
              <div class="spacey-12">
                <div v-if="project.description">
                  <div class="text-bsb">{{ $t('Description') }}</div>
                  {{ project.description }}
                </div>
                <div v-if="project.customer">
                  <span class="text-bsb">{{ $t('Customer') }}</span> - {{ project.customer }}
                </div>
                <div>
                  <span class="text-bsb">{{ $t('Execution address') }}</span> -
                  {{ project.address }}
                </div>
                <div v-if="project.price_submission_date">
                  <span class="text-bsb">{{ $t('Price submission date') }}</span> -
                  {{ prettyDates(project.price_submission_date) }}
                </div>
                <div v-if="project.construction_beginning_date">
                  <span class="text-bsb">{{ $t('Construction beginning date') }}</span> -
                  {{ prettyDates(project.construction_beginning_date) }}
                </div>
              </div>
            </InfoTooltip>
          </router-link>
          <div class="spacex-16 text-sm grey-500">
            <span class="no-wrap">
              {{ $t('Project stage') }}
              <span class="primary-600">{{ project_states[project.state] }}</span>
            </span>
            <span v-if="responsibleUser" class="no-wrap">
              {{ $t('Responsible person') }}
              <span class="primary-600">{{ responsibleUser }}</span>
            </span>
            <span v-if="project.currency">
              <span class="no-wrap">{{ currencyWithSymbol(project.currency) }}</span>
            </span>
          </div>
        </div>
        <div class="project-card__members">
          <MemberAvatars :members="members" />
        </div>
        <div class="project-card__toggle">
          <button @click.stop="changeTendersDisplay">
            <Badge v-if="tendersCount" color="light-blue">
              {{ $tc('tender', tendersCount) }}
              <ChevronDown class="icon-g-900" :class="{ 'turn-over': showTenders }" />
            </Badge>
          </button>
        </div>
        <div class="project-card__date grey-500 text-sm spacex-10">
          <span>{{ $t('Edited') }} {{ prettyDates(project.updated_at, false, true) }}</span>
        </div>
        <div class="project-card__menu">
          <v-menu left offset-y transition="slide-y-transition" @click.stop>
            <template v-slot:activator="{ on, attrs }">
              <button v-bind="attrs" class="no-outline flex-centering" v-on="on">
                <HorizontalDotsIcon class="pointer" />
              </button>
            </template>
            <div class="dropdown-body">
              <DropdownMenuElement @click="openProjectSettings">
                {{ $t('Project settings') }}
                <template #icon>
                  <SettingsIcon class="icon-g-900" />
                </template>
              </DropdownMenuElement>
              <DropdownMenuElement v-if="isPossibleToArchive" @click="handleArchive">
                {{ $t('Archive project') }}
                <template #icon>
                  <ArchiveIcon class="icon-g-900" />
                </template>
              </DropdownMenuElement>
              <DropdownMenuElement v-else @click="handleUnarchive">
                {{ $t('Unarchive project') }}
                <template #icon>
                  <ArchiveIcon class="icon-g-900" />
                </template>
              </DropdownMenuElement>
            </div>
          </v-menu>
        </div>
      </router-link>
      <keep-alive>
        <div v-if="showTenders" class="project-card_table">
          <ProjectCardTenders :tenders="tenders" :project="project" @invite="handleInvite" />
        </div>
      </keep-alive>
    </div>
  </div>
</template>

<script>
  import MemberAvatars from '@/components/tenders/MemberAvatars';
  import Badge from '@/components/ui/Badge';
  import ChevronDown from '@/assets/icons/chevron-down.svg';
  import ArchiveIcon from '@/assets/icons/archive.svg';
  import HorizontalDotsIcon from '@/assets/icons/horizontal-dots.svg';
  import { VMenu } from 'vuetify/lib';
  import prettyDates from '@/utils/formatting/pretty-date';
  import DropdownMenuElement from '@/components/ui/dropdown-menu/DropdownMenuElement';
  import ProjectSettingsModal from '@/containers/top-menu/header/project-settings/Modal';
  import { mapActions, mapGetters, mapState } from 'vuex';
  import ProjectCardTenders from '@/containers/projects/ProjectCardTenders';
  import InfoTooltip from '@/components/ui/InfoTooltip';
  import editTender from '@/utils/mixins/edit-tender';
  import getProjectLink from '@/utils/mixins/get-project-link';
  import get from 'lodash.get';
  import SettingsIcon from '@/assets/icons/new-settings.svg';
  import request from '@/utils/api/request';
  import factory from '@/utils/api/factory';
  import { DEFAULT_COLOR } from '@/utils/constants/common';

  export default {
    name: 'ProjectCard',
    components: {
      MemberAvatars,
      Badge,
      ChevronDown,
      ArchiveIcon,
      VMenu,
      HorizontalDotsIcon,
      DropdownMenuElement,
      ProjectCardTenders,
      InfoTooltip,
      SettingsIcon,
    },
    mixins: [editTender, getProjectLink],
    props: {
      project: {
        type: Object,
        required: true,
      },
    },
    computed: {
      ...mapState('global', ['project_states']),
      ...mapGetters('global', ['currencyWithSymbol']),
      members() {
        return this.project.responsible_users.map((user) => user.user);
      },
      tendersCount() {
        return this.project.tenders_count || 0;
      },
      isPossibleToArchive() {
        return this.project.status !== 'archived';
      },
      responsibleUser() {
        return get(this.project, 'contact_person.name');
      },
    },
    data() {
      return {
        showTenders: false,
        tenders: [],
        DEFAULT_COLOR,
      };
    },
    methods: {
      ...mapActions('modal', ['openModal']),
      ...mapActions({
        openModal: 'modal/openModal',
        openConfirm: 'confirmModal/openModal',
        changeCurrentProject: 'global/changeCurrentProject',
      }),
      prettyDates,
      openProjectSettings() {
        this.openModal({
          component: ProjectSettingsModal,
          context: {
            createForm: false,
            projectId: this.project?.id,
            attributes: {
              class: 'create-tender__container',
            },
            update: () => {
              this.$emit('update');
            },
          },
        });
      },
      handleArchive() {
        this.openConfirm({
          title: 'Archive project',
          description: `Are you sure you want to archive project ${this.project.name}?`,
          actionText: 'Archive',
          action: async () => {
            try {
              await request.post(`/projects/${this.project.id}/archive/`);
              this.$emit('update');
            } catch (e) {
              console.error(e);
            }
          },
        });
      },
      handleUnarchive() {
        this.openConfirm({
          title: 'Unarchive project',
          description: `Are you sure you want to unarchive project ${this.project.name}?`,
          actionText: 'Unarchive',
          action: async () => {
            try {
              await request.post(`/projects/${this.project.id}/unarchive/`);
              this.$emit('update');
            } catch (e) {
              console.error(e);
            }
          },
        });
      },
      async changeTendersDisplay() {
        try {
          this.showTenders = !this.showTenders;
          if (!this.tenders.length) await this.loadTenders();
        } catch (e) {
          console.error(e);
        }
      },
      handleInvite(tenderId) {
        this.editTender(tenderId, 3, this.project);
      },
      async loadTenders() {
        let { data } = await factory('tenders').index({
          project_id: this.project.id,
          all: true,
        });

        this.tenders = data.results;
      },
    },
  };
</script>
<style lang="scss">
  @import 'src/styles/components/project-card';
</style>
